// src/pages/validateur/ValidationDashboard.jsx
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  User, 
  Package,
  ChevronRight,
  RefreshCw,
  Search,
  Filter,
  Calendar,
  Building,
  FileText,
  Eye,
  Check,
  X,
  Download,
  ChevronDown,
  ChevronUp,
  ExternalLink
} from 'lucide-react';
import { API_URL } from '../../Config';

export default function ValidationDashboard() {
  const [pending, setPending] = useState([]);
  const [validated, setValidated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('pending');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [expandedRow, setExpandedRow] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const loadData = async () => {
    try {
      const [resPending, resValidated] = await Promise.all([
        axios.get(`${API_URL}/validation/pending-demandes`, { headers }),
        axios.get(`${API_URL}/validation/validated-demandes`, { headers })
      ]);

      setPending(resPending.data.data?.data || resPending.data.data || []);
      setValidated(resValidated.data.data?.data || resValidated.data.data || []);
      setLastUpdate(new Date());
    } catch (err) {
      console.error('Erreur de chargement:', err);
      // En cas d'erreur, mettre des tableaux vides
      setPending([]);
      setValidated([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fonction de formatage de date
  const formatDate = (dateString) => {
    if (!dateString) return 'Date inconnue';
    try {
      return new Date(dateString).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Date invalide';
    }
  };

  // Fonction de tri
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Trier les données
  const sortedPending = [...pending].sort((a, b) => {
    if (sortConfig.key === 'created_at') {
      return sortConfig.direction === 'asc' 
        ? new Date(a.created_at) - new Date(b.created_at)
        : new Date(b.created_at) - new Date(a.created_at);
    }
    if (sortConfig.key === 'demandeur') {
      const nameA = a.demandeur?.nom || '';
      const nameB = b.demandeur?.nom || '';
      return sortConfig.direction === 'asc'
        ? nameA.localeCompare(nameB)
        : nameB.localeCompare(nameA);
    }
    return 0;
  });

  const sortedValidated = [...validated].sort((a, b) => {
    if (sortConfig.key === 'date_validation') {
      const dateA = a.date_validation || a.updated_at;
      const dateB = b.date_validation || b.updated_at;
      return sortConfig.direction === 'asc' 
        ? new Date(dateA) - new Date(dateB)
        : new Date(dateB) - new Date(dateA);
    }
    if (sortConfig.key === 'demandeur') {
      const nameA = a.demandeur?.nom || '';
      const nameB = b.demandeur?.nom || '';
      return sortConfig.direction === 'asc'
        ? nameA.localeCompare(nameB)
        : nameB.localeCompare(nameA);
    }
    return 0;
  });

  // Filtrer les données
  const filteredPending = sortedPending.filter(d => 
    (d.demandeur?.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.id.toString().includes(searchTerm) ||
    (d.numero && d.numero.toString().includes(searchTerm))) &&
    (!filterStatus || d.status === filterStatus)
  );

  const filteredValidated = sortedValidated.filter(d =>
    (d.demandeur?.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.id.toString().includes(searchTerm) ||
    (d.numero && d.numero.toString().includes(searchTerm)) ||
    d.status.toLowerCase().includes(searchTerm.toLowerCase())) &&
    (!filterStatus || d.status === filterStatus)
  );

  const getStatusBadge = (status) => {
    const config = {
      'validee': { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', icon: <Check className="h-3 w-3" /> },
      'refusee': { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: <X className="h-3 w-3" /> },
      'en_attente': { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: <Clock className="h-3 w-3" /> },
      'en_preparation': { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', icon: <Clock className="h-3 w-3" /> },
      'pret_recuperation': { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', icon: <CheckCircle className="h-3 w-3" /> },
      'annulee': { bg: '', text: 'text-gray-700', border: 'border-gray-200', icon: <X className="h-3 w-3" /> },
    };
    
    const statusConfig = config[status] || config.en_attente;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.text} ${statusConfig.border} border`}>
        {statusConfig.icon}
        {status === 'validee' ? 'Validée' : 
         status === 'refusee' ? 'Refusée' : 
         status === 'en_attente' ? 'En attente' :
         status === 'en_preparation' ? 'En préparation' :
         status === 'pret_recuperation' ? 'Prêt à récupérer' :
         status === 'annulee' ? 'Annulée' : status}
      </span>
    );
  };

  const getTotalItems = (demande) => {
    if (!demande.demande_materiels) return 0;
    return demande.demande_materiels.reduce((sum, item) => sum + (item.quantite_demandee || 0), 0);
  };

  const getStatusCount = (status) => {
    if (activeTab === 'pending') {
      return pending.filter(d => d.status === status).length;
    }
    return validated.filter(d => d.status === status).length;
  };

  const toggleRow = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  if (loading) {
    return (
      <div className="min-h-screen  flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Chargement des demandes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen  py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Validation des Demandes</h1>
              <p className="mt-1 text-gray-600 text-sm">
                Interface de validation des demandes de matériel
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={loadData}
                className="flex items-center gap-2 px-3 py-2 bg-white text-gray-700 rounded-lg border hover: transition-colors text-sm"
              >
                <RefreshCw className="h-4 w-4" />
                Actualiser
              </button>
            </div>
          </div>
          
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
            <div className="bg-white rounded-lg border p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">En attente</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{pending.length}</p>
                </div>
                <div className="p-2 bg-amber-50 rounded-lg">
                  <Clock className="h-5 w-5 text-amber-600" />
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg border p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Validées</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {getStatusCount('validee')}
                  </p>
                </div>
                <div className="p-2 bg-green-50 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg border p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Refusées</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {getStatusCount('refusee')}
                  </p>
                </div>
                <div className="p-2 bg-red-50 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg border p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Dernière MAJ</p>
                  <p className="text-sm font-medium text-gray-900 mt-1">
                    {lastUpdate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
                <Calendar className="h-5 w-5 text-gray-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('pending')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'pending'
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  En attente ({pending.length})
                </div>
              </button>
              <button
                onClick={() => setActiveTab('validated')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'validated'
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  Traitées ({validated.length})
                </div>
              </button>
            </nav>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher par nom, ID ou statut..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 appearance-none bg-white"
                >
                  <option value="">Tous les statuts</option>
                  {activeTab === 'pending' ? (
                    <>
                      <option value="en_attente">En attente</option>
                    </>
                  ) : (
                    <>
                      <option value="validee">Validées</option>
                      <option value="refusee">Refusées</option>
                      <option value="en_preparation">En préparation</option>
                      <option value="pret_recuperation">Prêt à récupérer</option>
                      <option value="annulee">Annulées</option>
                    </>
                  )}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Tableaux */}
        {activeTab === 'pending' ? (
          /* Tableau des demandes en attente */
          <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ID Demande
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('demandeur')}
                    >
                      <div className="flex items-center gap-1">
                        Demandeur
                        {sortConfig.key === 'demandeur' && (
                          sortConfig.direction === 'asc' ? 
                            <ChevronUp className="h-4 w-4" /> : 
                            <ChevronDown className="h-4 w-4" />
                        )}
                      </div>
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('created_at')}
                    >
                      <div className="flex items-center gap-1">
                        Date
                        {sortConfig.key === 'created_at' && (
                          sortConfig.direction === 'asc' ? 
                            <ChevronUp className="h-4 w-4" /> : 
                            <ChevronDown className="h-4 w-4" />
                        )}
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Matériels
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quantité Totale
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredPending.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center">
                          <CheckCircle className="h-12 w-12 text-gray-300 mb-4" />
                          <p className="text-gray-500">Aucune demande en attente</p>
                          {searchTerm && (
                            <p className="text-sm text-gray-400 mt-2">
                              Aucun résultat pour "{searchTerm}"
                            </p>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredPending.map((demande) => (
                      <>
                        <tr 
                          key={demande.id} 
                          className="hover: transition-colors cursor-pointer"
                          onClick={() => toggleRow(demande.id)}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <FileText className="h-4 w-4 text-gray-400 mr-2" />
                              <span className="text-sm font-medium text-gray-900">
                                {demande.numero ? `DEM-${demande.numero}` : `DEM-${demande.id.toString().padStart(4, '0')}`}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{demande.demandeur?.nom || 'N/A'}</div>
                            {demande.demandeur?.departement && (
                              <div className="text-xs text-gray-500">{demande.demandeur.departement}</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">{formatDate(demande.created_at)}</div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <Package className="h-4 w-4 text-gray-400 mr-2" />
                              <span className="text-sm text-gray-700">
                                {demande.demande_materiels?.length || 0} article(s)
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {getTotalItems(demande)} unités
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center gap-2">
                              <Link
                                to={`/validateur/demandes/${demande.id}`}
                                className="inline-flex items-center gap-2 px-3 py-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors text-sm"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Eye className="h-4 w-4" />
                                Examiner
                              </Link>
                              <button
                                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleRow(demande.id);
                                }}
                              >
                                {expandedRow === demande.id ? (
                                  <ChevronUp className="h-4 w-4" />
                                ) : (
                                  <ChevronDown className="h-4 w-4" />
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                        
                        {/* Row détaillée */}
                        {expandedRow === demande.id && (
                          <tr className="bg-blue-50">
                            <td colSpan="6" className="px-6 py-4">
                              <div className="pl-8 border-l-4 border-blue-200">
                                <div className="flex items-center justify-between mb-3">
                                  <h4 className="text-sm font-semibold text-gray-900">
                                    Détails des matériels
                                  </h4>
                                  <Link
                                    to={`/validateur/demandes/${demande.id}`}
                                    className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    Valider maintenant
                                    <ExternalLink className="h-3 w-3" />
                                  </Link>
                                </div>
                                
                                {demande.demande_materiels?.length > 0 ? (
                                  <div className="space-y-2 max-h-60 overflow-y-auto">
                                    {demande.demande_materiels.map((item, idx) => (
                                      <div key={idx} className="text-sm bg-white p-3 rounded border">
                                        <div className="flex justify-between items-start mb-1">
                                          <span className="font-medium text-gray-900">
                                            {item.materiel?.nom || 'Matériel inconnu'}
                                          </span>
                                          <span className="text-xs text-gray-500">
                                            {item.quantite_demandee} {item.materiel?.unite || 'unité'}
                                          </span>
                                        </div>
                                        <div className="text-gray-600 text-xs">
                                          Réf: {item.materiel?.reference || 'N/A'} • 
                                          Stock: {item.materiel?.stock || 0}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="text-sm text-gray-500 italic">
                                    Aucun matériel dans cette demande
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          /* Tableau des demandes traitées */
          <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ID Demande
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Demandeur
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date Demande
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('date_validation')}
                    >
                      <div className="flex items-center gap-1">
                        Date Traitement
                        {sortConfig.key === 'date_validation' && (
                          sortConfig.direction === 'asc' ? 
                            <ChevronUp className="h-4 w-4" /> : 
                            <ChevronDown className="h-4 w-4" />
                        )}
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statut
                    </th>
                    
                   
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredValidated.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center">
                          <FileText className="h-12 w-12 text-gray-300 mb-4" />
                          <p className="text-gray-500">Aucune demande traitée</p>
                          {searchTerm && (
                            <p className="text-sm text-gray-400 mt-2">
                              Aucun résultat pour "{searchTerm}"
                            </p>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredValidated.map((demande) => (
                      <tr key={demande.id} className="hover: transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <FileText className="h-4 w-4 text-gray-400 mr-2" />
                            <span className="text-sm font-medium text-gray-900">
                              {demande.numero ? `DEM-${demande.numero}` : `DEM-${demande.id.toString().padStart(4, '0')}`}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{demande.demandeur?.nom || 'N/A'}</div>
                          {demande.demandeur?.departement && (
                            <div className="text-xs text-gray-500">{demande.demandeur.departement}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {formatDate(demande.created_at)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {formatDate(demande.date_validation || demande.updated_at)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(demande.status)}
                        </td>
                        
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Pagination (exemple) */}
            {filteredValidated.length > 0 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    Affichage de 1 à {Math.min(10, filteredValidated.length)} sur {filteredValidated.length} résultats
                  </div>
                  <div className="flex items-center gap-2">
                    <button 
                      className="px-3 py-1 border rounded text-sm hover: disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled
                    >
                      Précédent
                    </button>
                    <button className="px-3 py-1 border rounded text-sm  font-medium">
                      1
                    </button>
                    <button className="px-3 py-1 border rounded text-sm hover:">
                      2
                    </button>
                    <button className="px-3 py-1 border rounded text-sm hover:">
                      Suivant
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Footer avec statistiques */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="text-sm text-gray-500">
              {activeTab === 'pending' 
                ? `${filteredPending.length} demande(s) en attente`
                : `${filteredValidated.length} demande(s) traitées`}
            </div>
            <div className="text-sm text-gray-500">
              Dernière actualisation: {formatDate(lastUpdate)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}